// src/types/index.ts
export interface DeliveryRecord {
  id?: string;
  date: string;
  platform: string;
  totalRevenue: number;
  deliveryCount: number;
  startTime: string;      // 追加: "09:00" 形式
  endTime: string;        // 追加: "18:30" 形式
  breakMinutes: number;   // 追加: 休憩時間(分)
  workingMinutes?: number; // このプラットフォームで実際に稼働した時間(分)
  notes: string;
  createdAt: number;
}

export interface Expense {
  id?: string;
  date: string;
  category: string;
  amount: number;
  description: string;
  createdAt: number;
}

export interface DeliveryPlatform {
  id?: string;
  name: string;
  color: string;
  isActive: boolean;
  createdAt: number;
}

export interface ExpenseCategory {
  id?: string;
  name: string;
  color: string;
  icon: string;  
  isActive: boolean;
  createdAt: number;
}

export interface RestDay {
  date: string;
  reason: string;
}

// ヘルパー関数: 実働時間を計算
export const calculateWorkingMinutes = (
  startTime: string, 
  endTime: string, 
  breakMinutes: number
): number => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  const startTotalMin = startHour * 60 + startMin;
  let endTotalMin = endHour * 60 + endMin;
  if (endTotalMin <= startTotalMin) {
    endTotalMin += 24 * 60;
  }
  
  const totalMin = endTotalMin - startTotalMin;
  return Math.max(0, totalMin - breakMinutes);
};

// ヘルパー関数: 配達記録から実働時間を計算
export const calculateActualWorkingMinutes = (
  deliveries: DeliveryRecord[]
): number => {
  if (deliveries.length === 0) return 0;

  const deliveriesWithDirectMinutes = deliveries
    .filter(d => typeof d.workingMinutes === 'number' && d.workingMinutes > 0);
  const directTotalMinutes = deliveriesWithDirectMinutes
    .reduce((sum, d) => sum + (d.workingMinutes || 0), 0);
  const deliveriesWithoutDirectMinutes = deliveries
    .filter(d => !(typeof d.workingMinutes === 'number' && d.workingMinutes > 0));

  if (deliveriesWithoutDirectMinutes.length === 0) {
    return directTotalMinutes;
  }
  
  // 時間範囲を取得
  const timeRanges = deliveriesWithoutDirectMinutes.map(d => {
    if (!d.startTime || !d.endTime) return null;
    const [startHour, startMin] = d.startTime.split(':').map(Number);
    const [endHour, endMin] = d.endTime.split(':').map(Number);
    let end = endHour * 60 + endMin;
    const start = startHour * 60 + startMin;
    if (end <= start) {
      end += 24 * 60;
    }
    return {
      start,
      end,
      breakMinutes: d.breakMinutes || 0
    };
  }).filter(r => r !== null) as Array<{start: number, end: number, breakMinutes: number}>;
  
  if (timeRanges.length === 0) return directTotalMinutes;
  
  // 時間範囲をマージして重複を除外
  const mergedRanges: Array<{start: number, end: number}> = [];
  const sortedRanges = [...timeRanges].sort((a, b) => a.start - b.start);
  
  let current = { start: sortedRanges[0].start, end: sortedRanges[0].end };
  
  for (let i = 1; i < sortedRanges.length; i++) {
    const range = sortedRanges[i];
    
    // 重複または隣接している場合はマージ
    if (range.start <= current.end) {
      current.end = Math.max(current.end, range.end);
    } else {
      // 重複していない場合は新しい範囲として追加
      mergedRanges.push(current);
      current = { start: range.start, end: range.end };
    }
  }
  mergedRanges.push(current);
  
  // マージされた範囲の合計時間を計算
  const totalMinutes = mergedRanges.reduce((sum, range) => {
    return sum + (range.end - range.start);
  }, 0);
  
  // 最大の休憩時間を引く（重複してカウントしない）
  const maxBreak = Math.max(...timeRanges.map(r => r.breakMinutes));
  
  return directTotalMinutes + Math.max(0, totalMinutes - maxBreak);
};
// src/types/index.ts に以下を追加

// ========== ランキング関連の型 ==========
// ========== ランキング関連の型 ==========

export interface RankingRecord {
  id?: string;
  userId: string;
  userName: string;
  date: string;
  revenue: number;
  deliveryCount: number;
  workingMinutes: number;
  platform: string;
  createdAt: number;
  goodCount: number;
  badCount: number;
  isExcluded: boolean;
}

export interface RankingSettings {
  isEnabled: boolean;
  userName: string;
}

export interface Vote {
  userId: string;
  recordId: string;
  type: 'good' | 'bad';
  createdAt: number;
}

export type RankingPeriod = 'today' | 'week' | 'month';

export interface RankingEntry extends RankingRecord {
  rank: number;
  hourlyRate: number;
  isCurrentUser: boolean;
}
